import dayjs from 'dayjs'

/**
 * Formats a date for display. Defaults to `MM/DD/YYYY`. Returns `'N/A'`
 * for null, undefined, or unparseable input. Use for display surfaces
 * only — not for form values or API payloads.
 *
 * Pass `format` to override the default pattern (e.g. when a surface
 * has a deliberate visual variant). Prefer the default everywhere else
 * to keep date display consistent across the app.
 *
 * @param {string|Date|dayjs.Dayjs} value
 * @param {string} [format='MM/DD/YYYY'] - dayjs format string
 * @returns {string}
 *
 * @example
 *   formatDate(user.createdAt)              // → '05/02/2026'
 *   formatDate(null)                        // → 'N/A'
 *   <span>Created: {formatDate(user.createdAt)}</span>
 */
export const formatDate = (value, format = 'MM/DD/YYYY') => {
  if (!value) return 'N/A'
  const d = dayjs(value)
  return d.isValid() ? d.format(format) : 'N/A'
}

/**
 * Formats a date-time for display in the browser's local timezone.
 * Defaults to `MM/DD/YYYY h:mm A`. Returns `'N/A'` for null, undefined,
 * or unparseable input. Use for display surfaces only — not for form
 * values or API payloads.
 *
 * Pass `format` to override the default pattern (e.g. RoIntake tables
 * use leading-zero hours: `MM/DD/YYYY hh:mm A`). Prefer the default
 * everywhere else to keep datetime display consistent across the app.
 *
 * @param {string|Date|dayjs.Dayjs} value
 * @param {string} [format='MM/DD/YYYY h:mm A'] - dayjs format string
 * @returns {string}
 *
 * @example
 *   formatDateTime(ro.lastUpdatedOn)                       // → '05/02/2026 3:42 PM'
 *   formatDateTime(ro.receivedAt, 'MM/DD/YYYY hh:mm A')    // → '05/02/2026 03:42 PM'
 *   formatDateTime(undefined)                              // → 'N/A'
 *   <span>Last Updated: {formatDateTime(ro.lastUpdatedOn)}</span>
 */
export const formatDateTime = (value, format = 'MM/DD/YYYY h:mm A') => {
  if (!value) return 'N/A'
  const d = dayjs(value)
  return d.isValid() ? d.format(format) : 'N/A'
}

/**
 * Calculates the number of business days (Mon–Fri) between two dates.
 * @param {string|Date|dayjs.Dayjs} startDate
 * @param {string|Date|dayjs.Dayjs} endDate
 * @returns {number} business-day count, excluding today
 */
export const calculateBusinessDays = (startDate, endDate) => {
  const end = dayjs(endDate)

  let count = 0
  let current = dayjs(startDate)

  // Continue counting business days until we reach the end date
  while (current.isBefore(end)) {
    // Only count weekdays (Monday to Friday)
    if (current.day() !== 0 && current.day() !== 6) {
      count += 1
    }
    current = current.add(1, 'day')
  }

  // Subtract 1 to exclude today; clamp at 0 so same-day/future starts don't
  // return a negative count.
  // TODO: may need to be updated once we fix the model field to be a datetime
  return Math.max(0, count - 1)
}

export const formatPhoneNumber = (phoneNumberString) => {
  if (phoneNumberString) {
    const cleaned = ('', phoneNumberString).replace(/\D/g, '')
    const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)
    if (match) {
      const intlCode = match[1] ? '+1 ' : ''
      return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('')
    }
  }
  return ''
}

export const formatMonthName = (monthNumber) => {
  if (!monthNumber) return ''
  // Create a dayjs object using the first day of the given month
  return dayjs()
    .month(monthNumber - 1)
    .format('MMMM')
}

export const formatCurrency = (amount, round = true) => {
  const numAmount = Number(amount || 0)
  const isNegative = numAmount < 0
  const absAmount = Math.abs(numAmount)

  const formatted = absAmount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: round ? 0 : 2,
    maximumFractionDigits: round ? 0 : 2,
  })

  return isNegative ? `(${formatted})` : formatted
}
